import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Award, Clock, CheckCircle2, ArrowRight, FileText, Users, QrCode, Shield, Scan, Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { QRCodeSVG } from "qrcode.react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Demo certificates for QR code showcase
const DEMO_CERTIFICATES = [
  {
    id: "demo-cert-001",
    certificate_number: "ZWII-2025-DEMO001",
    holder_name: "John Mitchell",
    program_title: "Workforce Intelligence Professional (WIP)",
    status: "valid"
  },
  {
    id: "demo-cert-002",
    certificate_number: "ZWII-2025-DEMO002",
    holder_name: "Sarah Chen",
    program_title: "AI Workforce Integration Specialist",
    status: "valid"
  },
  {
    id: "demo-cert-004",
    certificate_number: "ZWII-2025-DEMO004",
    holder_name: "Emily Rodriguez",
    program_title: "Quality & Safety Management Certificate",
    status: "valid"
  }
];

// QR Code Preview Component
function CertificateQRPreview({ certificate }) {
  const verificationUrl = `${window.location.origin}/verify-certificate/${certificate.id}`;
  const [downloading, setDownloading] = useState(false);
  
  // Download PDF handler
  const handleDownloadPDF = async (e) => {
    e.preventDefault();
    try {
      setDownloading(true);
      const response = await axios.get(
        `${API_URL}/api/certificates/${certificate.id}/pdf`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Zodiac_Certificate_${certificate.holder_name.replace(/\s+/g, '_')}_${certificate.certificate_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download error:", err);
      alert("Failed to download certificate. Please try again.");
    } finally {
      setDownloading(false);
    }
  };
  
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="gap-2 text-[#1E3A8A] border-[#1E3A8A]/30 hover:bg-[#1E3A8A]/5"
          data-testid={`qr-preview-btn-${certificate.id}`}
        >
          <QrCode className="w-4 h-4" />
          View QR
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="qr-preview-dialog">
        <DialogHeader>
          <DialogTitle className="text-center text-[#1E3A8A]">Certificate QR Code</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center p-6">
          {/* QR Code */}
          <div className="bg-white p-4 rounded-xl shadow-inner border-2 border-gray-100" data-testid="qr-code-container">
            <QRCodeSVG 
              value={verificationUrl}
              size={200}
              level="H"
              includeMargin={true}
              bgColor="#FFFFFF"
              fgColor="#1E3A8A"
            />
          </div>
          
          {/* Certificate Info */}
          <div className="mt-6 text-center w-full">
            <p className="font-mono text-sm text-gray-500 mb-2">{certificate.certificate_number}</p>
            <p className="font-semibold text-gray-900">{certificate.holder_name}</p>
            <p className="text-sm text-gray-600 mt-1">{certificate.program_title}</p>
            
            <div className="mt-4 flex items-center justify-center gap-2">
              <Shield className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-600">Verified Certificate</span>
            </div>
          </div>
          
          {/* Download PDF Button */}
          <Button 
            onClick={handleDownloadPDF}
            disabled={downloading}
            className="mt-6 w-full bg-[#991B1B] hover:bg-[#7F1D1D]"
            data-testid={`download-pdf-btn-${certificate.id}`}
          >
            {downloading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating PDF...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Download Certificate PDF
              </>
            )}
          </Button>
          
          {/* Instructions */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg w-full">
            <div className="flex items-start gap-2 text-sm text-blue-800">
              <Scan className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p>Scan this QR code with any mobile device to instantly verify this certificate's authenticity.</p>
            </div>
          </div>
          
          {/* Test Link */}
          <Link 
            to={`/verify-certificate/${certificate.id}`}
            className="mt-4 text-sm text-[#1E3A8A] hover:underline"
            data-testid="test-verification-link"
          >
            Test verification page →
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function TrainingCertificates() {
  const [demoCertificates, setDemoCertificates] = useState(DEMO_CERTIFICATES);
  const [seedingDemo, setSeedingDemo] = useState(false);

  // Seed demo certificates on first load
  useEffect(() => {
    const seedDemoCertificates = async () => {
      try {
        setSeedingDemo(true);
        await axios.post(`${API_URL}/api/certificates/seed-demo`);
      } catch (err) {
        // Ignore if already seeded
        console.log("Demo certificates already exist or seeding failed");
      } finally {
        setSeedingDemo(false);
      }
    };
    
    seedDemoCertificates();
  }, []);

  const certificates = [
    {
      title: "Workforce Intelligence Professional (WIP)",
      description: "Master the fundamentals of workforce analytics, diagnostic instruments, and organizational assessment.",
      duration: "4 months",
      modules: 8,
      level: "Foundation"
    },
    {
      title: "AI Workforce Integration Specialist",
      description: "Learn to strategically integrate AI capabilities into workforce planning and operations.",
      duration: "3 months",
      modules: 6,
      level: "Advanced"
    },
    {
      title: "Organizational Resilience Practitioner",
      description: "Develop expertise in change management, workforce transformation, and organizational adaptability.",
      duration: "4 months",
      modules: 8,
      level: "Advanced"
    },
    {
      title: "Quality & Safety Management Certificate",
      description: "Comprehensive training in TQM principles, safety management systems, and compliance frameworks.",
      duration: "6 months",
      modules: 12,
      level: "Professional"
    }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="training-certificates-page">
      {/* Hero */}
      <section className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/programs" className="inline-flex items-center gap-2 text-[#1E3A8A] hover:text-[#991B1B] mb-6 text-sm">
              <ArrowRight className="w-4 h-4 rotate-180" />
              Back to Programs
            </Link>
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E3A8A]/10 text-[#1E3A8A] text-sm font-medium mb-6">
                <Award className="w-4 h-4" />
                Professional Credentials
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-6" style={{ color: '#991B1B' }}>
                Training Certificates
              </h1>
              <p className="text-lg text-[#1E3A8A] leading-relaxed">
                Earn industry-recognized credentials in workforce intelligence, AI integration, 
                quality management, and organizational development.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Certificates Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {certificates.map((cert, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-[#E5E7EB] bg-white hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-xl bg-[#1E3A8A]/10 flex items-center justify-center">
                        <Award className="w-6 h-6 text-[#1E3A8A]" />
                      </div>
                      <span className="px-3 py-1 rounded-full bg-[#991B1B]/10 text-[#991B1B] text-xs font-medium">
                        {cert.level}
                      </span>
                    </div>
                    <h3 className="font-heading text-xl font-bold mb-3" style={{ color: '#991B1B' }}>
                      {cert.title}
                    </h3>
                    <p className="text-[#1E3A8A] text-sm mb-4 leading-relaxed">
                      {cert.description}
                    </p>
                    <div className="flex items-center gap-6 mb-6 text-sm text-[#1E3A8A]">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        {cert.duration}
                      </div>
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        {cert.modules} modules
                      </div>
                    </div>
                    <Link to="/admissions">
                      <Button className="w-full bg-[#1E3A8A] text-white hover:bg-[#1E40AF]">
                        Learn More
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* QR Verification Section */}
      <section className="py-16 bg-gradient-to-b from-[#1E3A8A]/5 to-white" data-testid="qr-verification-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E3A8A]/10 text-[#1E3A8A] text-sm font-medium mb-4">
              <QrCode className="w-4 h-4" />
              Instant Verification
            </div>
            <h2 className="font-heading text-3xl font-bold mb-4" style={{ color: '#991B1B' }}>
              QR Code Certificate Verification
            </h2>
            <p className="text-lg text-[#1E3A8A] max-w-2xl mx-auto">
              Every certificate includes a unique QR code that employers and institutions can scan 
              for instant verification of authenticity.
            </p>
          </motion.div>

          {/* How it Works */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-16 h-16 rounded-2xl bg-[#1E3A8A] flex items-center justify-center mx-auto mb-4">
                <QrCode className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-semibold text-lg mb-2 text-gray-900">1. Scan QR Code</h3>
              <p className="text-gray-600">Use any smartphone camera to scan the QR code on the certificate</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-16 h-16 rounded-2xl bg-[#991B1B] flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-semibold text-lg mb-2 text-gray-900">2. Instant Verification</h3>
              <p className="text-gray-600">Our secure system instantly verifies the certificate's authenticity</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-16 h-16 rounded-2xl bg-green-600 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-semibold text-lg mb-2 text-gray-900">3. View Details</h3>
              <p className="text-gray-600">See complete certificate details including holder name and program</p>
            </motion.div>
          </div>

          {/* Demo Certificates */}
          <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
            <h3 className="font-heading text-xl font-bold mb-6 text-center" style={{ color: '#1E3A8A' }}>
              Try It: Sample Certificate QR Codes
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {demoCertificates.map((cert) => (
                <div 
                  key={cert.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100"
                  data-testid={`demo-cert-${cert.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{cert.holder_name}</p>
                    <p className="text-sm text-gray-500 truncate">{cert.program_title}</p>
                    <p className="text-xs font-mono text-gray-400 mt-1">{cert.certificate_number}</p>
                  </div>
                  <div className="ml-4">
                    <CertificateQRPreview certificate={cert} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white border-t border-[#E5E7EB]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl font-bold mb-4" style={{ color: '#991B1B' }}>
            Advance Your Career
          </h2>
          <p className="text-lg text-[#1E3A8A] mb-8">
            Earn credentials that demonstrate your expertise in workforce intelligence.
          </p>
          <Link to="/admissions">
            <Button size="lg" className="bg-[#991B1B] text-white hover:bg-[#7F1D1D] h-12 px-8">
              Apply Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
